import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { ThankYouManager } from '@ctct/application-layer';
import type { OrderSummaryHooks, PayflowTopBarHooks } from '@ctct/state-client-layer';
import { useXeroPromotionGate, usePlanGroupFacade, useEmailAddresses, useAccountId } from '@ctct/state-server-layer';

interface Props {
  initializer: Pick<ThankYouManager, 'thankYouInit'> & {
    thankYouController?: {
      initializeListeners: (t: (key: string, params?: Record<string, string | number>) => string) => void;
      cleanup: () => void;
    };
  };
  externalHooks: {
    useReturnUrl: PayflowTopBarHooks['useReturnUrl'];
    useReturnLabel: PayflowTopBarHooks['useReturnLabel'];
    useIsXeroAddonSelected: OrderSummaryHooks['useIsXeroAddonSelected'];
  };
  version?: 'regular' | 'moreton-bay' | 'sms-bundle';
}

export const useThankYouWidget = ({ initializer, externalHooks, version = 'regular' }: Props) => {
  const { t } = useTranslation();
  const location = useLocation<{ firstName: string }>();

  const { data: xeroPartnerTurnedOn } = useXeroPromotionGate();
  // plan group should always exist in cache, so we don't handle loading state here
  const { data: planGroupQuery } = usePlanGroupFacade();
  const isSocialStarter = planGroupQuery?.isSocialStarter() ?? false;
  const isCurrentPlanEvents = planGroupQuery?.isCurrentPlanEvents() ?? false;

  // Get email verification status
  const { data: emailAddresses } = useEmailAddresses();
  const hasVerifiedEmail = emailAddresses?.some((email) => email.isConfirmed()) ?? true;
  const billingEmail = emailAddresses?.find((email) => email.getRoles().includes('BILLING'));
  const billingEmailId = billingEmail?.getEmailId();
  const billingEmailAddress = billingEmail?.getEmailAddress();

  // Get Site Owner ID (SOUID) for conversion tracking
  const { data: accountId } = useAccountId();
  const soid = accountId ? String(accountId) : undefined;

  const flags = useMemo(
    () => ({
      xeroPartnerTurnedOn: xeroPartnerTurnedOn ?? false,
      isSocialStarter,
      isCurrentPlanEvents,
      hasVerifiedEmail,
      billingEmailId,
      billingEmailAddress,
      soid,
    }),
    [
      xeroPartnerTurnedOn,
      isSocialStarter,
      isCurrentPlanEvents,
      hasVerifiedEmail,
      billingEmailId,
      billingEmailAddress,
      soid,
    ]
  );

  // Initialize ThankYouController listeners with translation function
  useEffect(() => {
    if (!initializer.thankYouController) {
      return undefined;
    }

    initializer.thankYouController.initializeListeners(t);

    return () => {
      initializer.thankYouController?.cleanup();
    };
  }, [initializer.thankYouController, t]);

  const componentProps = useMemo(
    () => ({
      ...initializer.thankYouInit({
        t,
        locationState: location.state,
        version,
        flags,
      }),
      externalHooks,
      version,
      flags,
    }),
    [initializer, t, location.state, externalHooks, version, flags]
  );

  return componentProps;
};

export type ThankYouWidgetProps = ReturnType<typeof useThankYouWidget>;
